/**
 * The document outline: every heading, indented, click to go there.
 *
 * WHAT IT IS FOR. A forty-page procedure is navigated by its clause numbers -- "go to 7.3" -- and
 * until now the only way to do that was to scroll. This is the control a reader of a long document
 * reaches for first, and the one a writer uses to see the SHAPE of what they have written: a
 * section with nine subsections and one with none is visible here and invisible on the page.
 *
 * THE NUMBERS ARE THE DOCUMENT'S OWN. outlineOf computes them, the renderer mirrors it, and the
 * table of contents uses the same call -- so a number in this pane is the number that prints. When
 * the document is not numbered the pane still lists the headings; it is an outline either way.
 *
 * NOT A SECOND TABLE OF CONTENTS. The TOC is part of the document and prints; this is chrome and
 * does not. They share the rule and nothing else.
 */
import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";

import Symbol from "./Symbol";
import { outlineOf } from "./outline";
import { currentPlace, placesOf } from "./outlineNav";
import { Blue3, Grey3, Grey5, Grey17 } from "../../theme/styles";

const CHROME_FONT = "'Google Sans', Roboto, Arial, sans-serif";

export default function OutlinePane({ editor, editorState, onClose }: {
    editor: any;
    /** Bumped on every transaction, so the pane follows the document as it is written. */
    editorState?: number;
    onClose: () => void;
}) {
    const places = React.useMemo(
        () => placesOf(editor?.state?.doc),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [editor, editorState]);

    const numbers = React.useMemo(() => {
        const body = editor?.getJSON?.();
        if (!body) return [];
        return outlineOf(body, !!editor?.state?.doc?.attrs?.outlineNumbering);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, editorState]);

    const here = currentPlace(places, editor?.state?.selection?.from ?? 0);

    // MATCHED BY INDEX, AND CHECKED. Both lists are "top-level headings in order" from the same
    // document, so index is the join -- but if they ever disagree the numbers are dropped rather
    // than shown against the wrong heading, which is the one failure a reader cannot see.
    const numbered = numbers.length === places.length;

    const go = (pos: number) => {
        if (!editor) return;
        editor.chain().focus().setTextSelection(pos + 1).run();
        // SCROLLED BY THE DOM, not by the editor: setTextSelection moves the caret and leaves the
        // page where it was when the heading is far off screen.
        const dom = editor.view?.nodeDOM?.(pos) as HTMLElement | null;
        dom?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    };

    return (
        <Box sx={{ width: 260, flexShrink: 0, borderLeft: `1px solid ${Grey3}`,
                   display: "flex", flexDirection: "column", fontFamily: CHROME_FONT,
                   maxHeight: "calc(100vh - 160px)", position: "sticky", top: 8 }}>
            <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: 15, color: Grey17, flexGrow: 1 }}>Outline</Typography>
                <Tooltip title="Close outline">
                    <IconButton size="small" onClick={onClose} aria-label="Close outline">
                        <Symbol name="close" size={18}/>
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ overflowY: "auto", pb: 2 }}>
                {places.length === 0 ? (
                    // A DOCUMENT WITH NO HEADINGS IS NOT A BROKEN OUTLINE. Say what would put
                    // something here rather than showing an empty box.
                    <Typography sx={{ fontSize: 12, color: Grey5, px: 1.5, py: 1 }}>
                        No headings yet. Headings are what an outline is made of — style a line as
                        Heading 1 or 2 and it appears here.
                    </Typography>
                ) : places.map((place, index) => (
                    <Box key={`${place.pos}-${index}`}
                         role="button" tabIndex={0}
                         aria-current={index === here ? "true" : undefined}
                         onClick={() => go(place.pos)}
                         onKeyDown={event => event.key === "Enter" && go(place.pos)}
                         sx={{
                             display: "flex", gap: 0.75, cursor: "pointer",
                             // INDENTED BY LEVEL, capped: past the fourth level the indent costs
                             // more width than the nesting is worth on a 260px pane.
                             pl: 1.5 + Math.min(place.level - 1, 3) * 1.25, pr: 1.5, py: 0.5,
                             backgroundColor: index === here ? "#E8F0FE" : undefined,
                             "&:hover": { backgroundColor: index === here ? "#E8F0FE" : "#F1F3F4" },
                         }}>
                        {numbered && numbers[index]?.number && (
                            <Typography sx={{ fontSize: 12, color: Grey5, flexShrink: 0 }}>
                                {numbers[index].number}
                            </Typography>
                        )}
                        <Typography sx={{ fontSize: 13, color: index === here ? Blue3 : Grey17,
                                          overflow: "hidden", textOverflow: "ellipsis",
                                          whiteSpace: "nowrap" }}>
                            {/* AN UNTITLED HEADING IS STILL A PLACE TO GO. */}
                            {place.text || "Untitled section"}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
